/**
 * Transcript Service
 *
 * Real API implementation for transcript editing features.
 * Provides methods to:
 * - Update transcript words (with optimistic locking)
 * - Regenerate summary after transcript edits
 */

import { callExternalApi } from "./external-api.service";
import { getAccessTokenSilentlyOutsideComponent } from "./workspace.service";

const API_SERVER_URL = import.meta.env.VITE_API_SERVER_URL;

/**
 * Update words in an audio file transcript
 *
 * @param {string} workspaceUUID - Workspace identifier
 * @param {string} audioFileUUID - Audio file identifier
 * @param {Object} payload - Update payload
 * @param {number} payload.transcriptVersion - Current transcript version (for optimistic locking)
 * @param {Array} payload.updates - Array of word updates
 * @param {number} payload.updates[].index - Word index in the transcript
 * @param {string} payload.updates[].text - New text for the word (max 30 chars)
 * @returns {Promise<Object>} Response with success status, new version, and summaryStale flag
 */
export async function updateTranscriptWords(
  workspaceUUID,
  audioFileUUID,
  payload,
) {
  console.log("[TranscriptService] Updating transcript words:", {
    workspaceUUID,
    audioFileUUID,
    transcriptVersion: payload.transcriptVersion,
    updateCount: payload.updates.length,
  });

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/transcript/words`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilentlyOutsideComponent()}`,
    },
    data: payload,
  };

  const { data, error } = await callExternalApi({ config, skipCache: true });

  if (error) {
    console.error("[TranscriptService] Failed to update words:", error);

    // Re-throw with the original error to preserve status codes (e.g., 409 for conflicts)
    const updateError = new Error(error.message || "Failed to update words");
    updateError.status = error.status;
    throw updateError;
  }

  console.log("[TranscriptService] Update successful:", {
    newVersion: data.transcriptVersion,
    modifiedWords: data.modifiedWords,
    summaryStale: data.summaryStale,
  });

  return data;
}

/**
 * Regenerate summary after transcript edits
 *
 * @param {string} workspaceUUID - Workspace identifier
 * @param {string} audioFileUUID - Audio file identifier
 * @returns {Promise<Object>} Response with success status, new summary, and summaryStale flag
 */
export async function regenerateSummary(workspaceUUID, audioFileUUID) {
  console.log("[TranscriptService] Regenerating summary:", {
    workspaceUUID,
    audioFileUUID,
  });

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/summary/regenerate`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilentlyOutsideComponent()}`,
    },
  };

  const { data, error } = await callExternalApi({ config, skipCache: true });

  if (error) {
    console.error("[TranscriptService] Failed to regenerate summary:", error);

    const regenerateError = new Error(
      error.message || "Failed to regenerate summary",
    );
    regenerateError.status = error.status;
    throw regenerateError;
  }

  console.log("[TranscriptService] Summary regenerated successfully:", {
    summaryStale: data.summaryStale,
  });

  return data;
}
